import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Hash, MessageSquare } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Avatar } from "@/components/Avatar";
import { api, errMsg } from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import { RangeSelect, dash, mins, num, useRange } from "./shared";
import { ActivityTimeline } from "./ActivityTimeline";
import { SLA_OPTIONS } from "./Performance";

const TT = { fontSize: 12, fontFamily: "inherit", borderRadius: 6, background: "var(--tc-bg-elev)", border: "1px solid var(--tc-border)", color: "var(--tc-text)" };
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const Stat = ({ label, value, sub, testid }) => (
  <div className="tc-stat" data-testid={testid}><div className="tc-stat__label">{label}</div><div className="tc-stat__value">{value}</div>{sub && <div className="tc-stat__sub">{sub}</div>}</div>
);

const Heatmap = ({ grid }) => {
  const max = Math.max(1, ...grid.flat());
  return (
    <div className="tc-heat" data-testid="hour-heatmap">
      <div className="tc-heat__row tc-heat__row--head"><span /> {Array.from({ length: 24 }, (_, h) => <span key={h} className="tc-heat__h">{h % 3 === 0 ? h : ""}</span>)}</div>
      {grid.map((row, d) => (
        <div key={d} className="tc-heat__row"><span className="tc-heat__d">{DAYS[d]}</span>
          {row.map((n, h) => <span key={h} className="tc-heat__cell" title={`${DAYS[d]} ${h}:00 — ${num(n)} events`} style={{ opacity: n ? 0.15 + 0.85 * (n / max) : 1, background: n ? "var(--tc-aubergine, #4a154b)" : "var(--tc-divider)" }} />)}
        </div>
      ))}
    </div>
  );
};

export const PerformanceMember = ({ uid }) => {
  const { range, setRange, params } = useRange("30d");
  const [sla, setSla] = useState(15);
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    setD(null);
    api.get(`/admin/performance/members/${uid}`, { params: { ...params, sla_minutes: sla } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [uid, params, sla]);
  const m = d?.member;
  const daily = useMemo(() => (d?.daily || []).map((x) => ({ ...x, label: x.day.slice(5) })), [d]);
  const inbound = m ? m.mentions_received + m.dms_received : 0;
  const answered = m ? m.mentions_answered + m.dms_answered : 0;
  return (
    <div data-testid="performance-member">
      <button className="tc-btn tc-btn--ghost sm" onClick={() => navigate("/admin/performance")} data-testid="performance-back"><ArrowLeft size={14} /> All members</button>
      {m && (
        <div className="tc-member-head">
          <Avatar user={m.user} size={56} radius={10} />
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0 }}>{m.user.display_name}</h1>
            <div className="muted">{m.user.title ? `${m.user.title} · ` : ""}@{m.user.handle} · {m.user.email}{m.last_active ? ` · last active ${fmtRelative(m.last_active)}` : ""}</div>
          </div>
          <div className="tc-admin__toolbar" style={{ margin: 0 }}>
            <RangeSelect range={range} setRange={setRange} />
            <select className="tc-select" value={sla} onChange={(e) => setSla(Number(e.target.value))} data-testid="member-sla-select">{SLA_OPTIONS.map((x) => <option key={x} value={x}>{`SLA ${mins(x)}`}</option>)}</select>
            <button className="tc-btn tc-btn--outline sm" onClick={() => navigate(`/admin/events?user_id=${uid}`)} data-testid="member-open-events">Full event log</button>
          </div>
        </div>
      )}
      {!m && <p className="muted" style={{ padding: 24 }}>Loading…</p>}
      {m && (
        <>
          <div className="tc-stats">
            <Stat label="Messages" value={num(m.messages)} sub={`${num(m.replies)} thread replies`} testid="mstat-messages" />
            <Stat label="Responsiveness" value={inbound ? `${Math.round((answered / inbound) * 100)}%` : "—"} sub={`${num(answered)} of ${num(inbound)} DMs & mentions answered`} testid="mstat-responsiveness" />
            <Stat label="Median response" value={dash(m.response_median_min, mins)} sub={`p90 ${dash(m.response_p90_min, mins)} · ${num(m.responses_within_sla)} within SLA`} testid="mstat-response" />
            <Stat label="Active time" value={mins(m.active_minutes)} sub={`${mins(m.reading_minutes)} reading · ${num(m.sessions)} sessions`} testid="mstat-active" />
            <Stat label="Reactions" value={`${num(m.reactions_given)} / ${num(m.reactions_received)}`} sub="given / received" testid="mstat-reactions" />
            <Stat label="Typing" value={num(m.keystrokes)} sub={`${num(m.chars_typed)} chars · ${num(m.backspaces)} backspaces · ${num(m.drafts_abandoned)} drafts dropped`} testid="mstat-typing" />
            <Stat label="Clicks" value={num(m.clicks)} sub={`${num(m.events)} events total`} testid="mstat-clicks" />
            <Stat label="Collaboration" value={num(m.channels_active)} sub={`channels · ${num(m.files)} files · ${mins(m.huddle_minutes)} in huddles`} testid="mstat-collab" />
          </div>
          <div className="tc-admin-grid2">
            <div className="tc-admin-card">
              <div className="tc-admin-card__head">Daily activity<span className="right">messages · active minutes · keystrokes ÷ 100</span></div>
              <div className="tc-admin-card__body" style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={daily.map((x) => ({ ...x, kk: Math.round(x.keystrokes / 100) }))} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid stroke="var(--tc-divider)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#616061" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#616061" }} allowDecimals={false} />
                    <Tooltip contentStyle={TT} />
                    <Area type="monotone" dataKey="messages" stroke="#007a5a" fill="#007a5a" fillOpacity={0.18} strokeWidth={2} name="Messages" />
                    <Area type="monotone" dataKey="replies" stroke="#1264a3" fill="none" strokeWidth={2} name="Replies" />
                    <Area type="monotone" dataKey="active_minutes" stroke="#e8912d" fill="#e8912d" fillOpacity={0.12} strokeWidth={2} name="Active min" />
                    <Area type="monotone" dataKey="kk" stroke="#83388a" fill="none" strokeWidth={1.5} strokeDasharray="4 3" name="Keystrokes ÷100" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="tc-admin-card">
              <div className="tc-admin-card__head">Response time distribution<span className="right">DMs & mentions</span></div>
              <div className="tc-admin-card__body" style={{ height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={d.response_distribution} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid stroke="var(--tc-divider)" vertical={false} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#616061" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#616061" }} allowDecimals={false} />
                    <Tooltip contentStyle={TT} />
                    <Bar dataKey="count" fill="#1264a3" radius={[4, 4, 0, 0]} name="Responses" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <div className="tc-admin-grid2">
            <div className="tc-admin-card">
              <div className="tc-admin-card__head">When they're active<span className="right">events by weekday × hour (UTC)</span></div>
              <div className="tc-admin-card__body"><Heatmap grid={d.heatmap} /></div>
            </div>
            <div className="tc-admin-card">
              <div className="tc-admin-card__head">Where they post</div>
              <table className="tc-table" data-testid="member-top-conversations">
                <thead><tr><th>Conversation</th><th className="num">Messages</th><th>Last</th></tr></thead>
                <tbody>
                  {d.top_conversations.map((c) => (
                    <tr key={c.conversation_id} className="clickable" onClick={() => navigate(`/client/${c.conversation_id}`)}>
                      <td>{c.type === "channel" ? <><Hash size={12} style={{ display: "inline", verticalAlign: -1 }} /> {c.name}</> : <><MessageSquare size={12} style={{ display: "inline", verticalAlign: -1 }} /> {c.name}</>}</td>
                      <td className="num">{num(c.messages)}</td><td className="muted">{fmtRelative(c.last_active)}</td>
                    </tr>
                  ))}
                  {!d.top_conversations.length && <tr><td colSpan={3} className="muted" style={{ textAlign: "center", padding: 20 }}>No messages in this period.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <div className="tc-admin-grid2">
            <div className="tc-admin-card">
              <div className="tc-admin-card__head">What they do most</div>
              <table className="tc-table" data-testid="member-top-events"><tbody>
                {d.top_event_types.map((t) => <tr key={t.event_type}><td className="mono">{t.event_type}</td><td className="num">{num(t.count)}</td></tr>)}
              </tbody></table>
            </div>
            <div className="tc-admin-card">
              <div className="tc-admin-card__head">Most clicked elements</div>
              <table className="tc-table" data-testid="member-top-targets"><tbody>
                {d.top_click_targets.map((t) => <tr key={t.target}><td className="mono">{t.target}</td><td className="num">{num(t.count)}</td></tr>)}
                {!d.top_click_targets.length && <tr><td className="muted" style={{ padding: 20, textAlign: "center" }}>No clicks in this period.</td></tr>}
              </tbody></table>
            </div>
          </div>
          <ActivityTimeline uid={uid} params={params} types={d.top_event_types} />
        </>
      )}
    </div>
  );
};
